/**
 * DRPX Courier — Welche Nachweise laufen bald ab oder sind abgelaufen?
 * (Dashboard-Hinweis, 23.10.2026)
 *
 * Reine Hilfsfunktion ohne Datenbank, damit sie getestet werden kann. Die
 * gleiche Grenze (30 Tage) nutzt die Erinnerungs-Mail in der Edge Function
 * `check-zertifikate-ablauf`.
 *
 * @module lib/zertAblauf
 */

/** Ab so vielen Resttagen wird gewarnt. */
export const ABLAUF_WARN_TAGE = 30;

/** Ein Zertifikat, so wie `db.getFahrerZertifikate` es liefert (Ausschnitt). */
export interface ZertZeile {
  zertKey?: unknown;
  status?: unknown;
  gueltigBis?: unknown;
}

/** Ein Eintrag für den Hinweis. */
export interface ZertAblaufEintrag {
  zertKey: string;
  /** JJJJ-MM-TT — letzter gültiger Tag */
  gueltigBis: string;
  /** Tage bis zum letzten gültigen Tag (0 = heute letzter Tag, negativ = abgelaufen) */
  restTage: number;
  abgelaufen: boolean;
}

/** Tage zwischen zwei JJJJ-MM-TT-Daten (b − a), unabhängig von Zeitzonen. */
function tageZwischen(a: string, b: string): number {
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000);
}

/**
 * Nachweise, die in höchstens {@link ABLAUF_WARN_TAGE} Tagen ablaufen oder
 * schon abgelaufen sind. Berücksichtigt nur geprüfte Nachweise (Status
 * `gueltig` bzw. `abgelaufen`) mit Datum — was noch in Prüfung oder
 * abgelehnt ist, zeigt die Zertifikate-Seite selbst an.
 *
 * @param zeilen Eigene Zertifikate
 * @param heute  Heutiges Datum JJJJ-MM-TT (deutsche Zeit)
 * @returns Einträge, das dringendste zuerst
 */
export function zertAblaufListe(zeilen: ReadonlyArray<ZertZeile>, heute: string): ZertAblaufEintrag[] {
  const liste: ZertAblaufEintrag[] = [];
  for (const z of zeilen) {
    const key = typeof z.zertKey === 'string' ? z.zertKey : '';
    const status = typeof z.status === 'string' ? z.status : '';
    const bis = typeof z.gueltigBis === 'string' ? z.gueltigBis.slice(0, 10) : '';
    if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(bis)) continue;
    if (status !== 'gueltig' && status !== 'abgelaufen') continue;
    const rest = tageZwischen(heute, bis);
    const abgelaufen = rest < 0 || status === 'abgelaufen';
    if (!abgelaufen && rest > ABLAUF_WARN_TAGE) continue;
    liste.push({ zertKey: key, gueltigBis: bis, restTage: rest, abgelaufen });
  }
  return liste.sort((x, y) => x.restTage - y.restTage);
}
